import React, { useCallback, useMemo } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';
import { useTenant } from '../../data/entities';
import { apiGet, isBackendConfigured } from '../../utils/backendApi';
import useTelemetry from '../../components/command/useTelemetry';
import LiveFrame from '../live/LiveFrame';
import SecurityRollup from '../live/SecurityRollup';
import BehaviorBaseline from '../live/BehaviorBaseline';
import TrafficBoard from '../live/TrafficBoard';
import { ORG_LIVE, appLive } from '../live/routes';
import { useOrgDocs } from './AppsModule';

const COPY = {
  security: ['Security', 'Every app · actionable findings separate from the kit baseline'],
  traffic: ['Traffic', 'Edge requests and flows across the organization'],
  anomalies: ['Anomalies', 'How traffic is thresholded, and what is flagged'],
};

export default function OrgLivePage() {
  const { org } = useOutletContext();
  const { aspect } = useParams();
  const tenant = useTenant();
  const apps = useOrgDocs('appInstance', 'apps', org.id);
  const known = ORG_LIVE.includes(aspect);
  const wantsTelemetry = known && aspect !== 'security';
  const roster = useMemo(() => (apps || []).filter((a) => a.slug && a.status !== 'terminated').map((a) => ({ slug: a.slug, envRole: a.envRole })), [apps]);
  const fetchReal = useCallback((w) => (
    isBackendConfigured()
      ? apiGet(`/apps/${org.id}/telemetry?window=${w}${tenant.workspace ? `&workspaceId=${encodeURIComponent(tenant.workspace)}` : ''}`)
      : Promise.resolve(null)
  ), [org.id, tenant.workspace]);
  const { telemetry, controls } = useTelemetry({ surface: 'org-live', roster, fetchReal, enabled: wantsTelemetry && apps != null });
  const back = `/orgs/${org.id}/deployments?tab=instances`;
  const [title, meta] = COPY[aspect] || ['Monitoring', ''];
  const rows = useMemo(() => (apps || [])
    .filter((a) => a.status !== 'terminated' && a.envRole !== 'uat')
    .map((a) => ({
      key: a.id,
      name: a.name || a.slug,
      slug: a.slug,
      assurance: a.assurance === true,
      scanning: !!a.scanRunning,
      lastScanAt: a.scan?.lastScanAt || null,
      counts: a.scan?.counts || {},
      href: appLive(org.id, a.id, 'security'),
    })), [apps, org.id]);

  if (!known) {
    return (
      <LiveFrame back={back} backLabel="← Instances" title="Monitoring" meta="That view is not on the organization fleet.">
        <p className="cs-help">{ORG_LIVE.join(' · ')}</p>
      </LiveFrame>
    );
  }

  return (
    <LiveFrame back={back} backLabel="← Instances" title={title} meta={meta}>
      {apps == null && <p className="cs-help">Loading…</p>}
      {aspect === 'security' && apps != null && <SecurityRollup rows={rows} />}
      {aspect === 'traffic' && <TrafficBoard telemetry={telemetry} controls={controls} />}
      {aspect === 'anomalies' && <BehaviorBaseline telemetry={telemetry} controls={controls} />}
    </LiveFrame>
  );
}
